import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  MdShield,
  MdAirlineSeatReclineNormal,
  MdSentimentSatisfied,
  MdAcUnit,
  MdEventSeat,
  MdPhoneAndroid,
  MdLightbulbOutline,
  MdLocalParking,
  MdLock,
  MdDirectionsCar,
  MdCardGiftcard,
  MdBuild,
} from 'react-icons/md';
import { getFeatureLabel } from '../../utils/featureMapper';

interface FeatureGroup {
  label: string;
  labelKey: string;
  icon: React.ReactNode;
  keys: string[];
}

const GROUPS: FeatureGroup[] = [
  {
    label: 'Safety & Assistance',
    labelKey: 'feature_list.safety',
    icon: <MdShield />,
    keys: [
      'abs', 'esp', 'asr', 'hill_start', 'fatigue_warning', 'lane_keeping',
      'blind_spot', 'night_vision', 'emergency_braking', 'emergency_call',
      'traffic_sign', 'distance_warning', 'interior_mirror', 'hill_start_assist',
    ],
  },
  {
    label: 'Airbags',
    labelKey: 'feature_list.airbags',
    icon: <MdAirlineSeatReclineNormal />,
    keys: [
      'driver_airbag', 'front_airbags', 'front_side', 'front_side_additional',
      'curtain_airbags', 'knee_airbags',
    ],
  },
  {
    label: 'Comfort',
    labelKey: 'feature_list.comfort',
    icon: <MdSentimentSatisfied />,
    keys: [
      'cruise_control', 'adaptive', 'speed_limiter', 'standard',
      'electric_windows', 'electric_side_mirrors', 'electric_tailgate',
      'keyless_central_locking', 'light_sensor', 'rain_sensor', 'power_steering',
      'ambient_lighting', 'leather_steering_wheel', 'start_stop_system',
    ],
  },
  {
    label: 'Climate Control',
    labelKey: 'feature_list.climate',
    icon: <MdAcUnit />,
    keys: [
      'manual', 'automatic', '2_zone', '3_zone', '4_zone',
      'auxiliary_heating', 'heated_windshield', 'heated_steering_wheel',
      'heated_seats', 'ventilated_seats', 'massage_seats',
    ],
  },
  {
    label: 'Seating',
    labelKey: 'feature_list.seating',
    icon: <MdEventSeat />,
    keys: [
      'leather_seats', 'alcantara_seats', 'memory_seats', 'sports_seats',
      'armrest', 'lumbar_support',
      'front_heating', 'rear_heating', 'driver_heating', 'passenger_heating',
      'front_electric', 'rear_electric', 'driver_electric', 'passenger_electric',
    ],
  },
  {
    label: 'Multimedia & Tech',
    labelKey: 'feature_list.multimedia',
    icon: <MdPhoneAndroid />,
    keys: [
      'navigation_system', 'apple_carplay', 'android_auto', 'bluetooth',
      'usb', 'touchscreen', 'voice_control', 'multifunction_steering_wheel',
      'hands_free_system', 'head_up_display', 'digital_instrument_cluster',
      'onboard_computer', 'sound_system', 'tuner_radio', 'radio_dab',
      'wlan_wifi_hotspot', 'music_streaming', 'music_streaming_integrated',
      'wireless_charging', 'induction_charging_smartphones', 'tv',
      'aux_input', 'hdmi_input', 'nfc', 'mirrorlink',
    ],
  },
  {
    label: 'Lights',
    labelKey: 'feature_list.lights',
    icon: <MdLightbulbOutline />,
    keys: [
      'halogen', 'xenon', 'bi_xenon', 'led', 'laser',
      'headlight_cleaning', 'glare_free_highbeam', 'highbeam_assistant', 'front_fog',
    ],
  },
  {
    label: 'Parking',
    labelKey: 'feature_list.parking',
    icon: <MdLocalParking />,
    keys: [
      'front_sensors', 'rear_sensors', 'side_sensors', 'front_rear',
      'rear_camera', '360_camera', 'front_camera', 'birdseye_view',
      'parking_sensors', 'rear_view_camera',
    ],
  },
  {
    label: 'Security',
    labelKey: 'feature_list.security',
    icon: <MdLock />,
    keys: [
      'alarm_system', 'electric_immobilizer', 'steering_lock', 'central_locking',
      'isofix', 'isofix_passenger',
    ],
  },
  {
    label: 'Exterior & Wheels',
    labelKey: 'feature_list.exterior',
    icon: <MdDirectionsCar />,
    keys: [
      'alloy_wheels', 'summer_tires', 'winter_tires', 'all_season_tires',
      'run_flat_tires', 'sunroof', 'panoramic_roof', 'roof_rails',
    ],
  },
  {
    label: 'Special Packages',
    labelKey: 'feature_list.packages',
    icon: <MdCardGiftcard />,
    keys: [
      'winter_package', 'sports_package', 'sports_suspension', 'air_suspension',
      'luxury_package', 'comfort_package', 'smokers_package',
    ],
  },
];

interface FeatureListProps {
  features: string[];
}

const FeatureList: React.FC<FeatureListProps> = ({ features }) => {
  const { t } = useTranslation();
  if (!features || features.length === 0) return null;

  const featSet = new Set(features);

  const renderedGroups = GROUPS.map((group) => {
    const matched = group.keys.filter((k) => featSet.has(k));
    return { ...group, matched };
  }).filter((g) => g.matched.length > 0);

  const groupedKeys = new Set(GROUPS.flatMap((g) => g.keys));
  const ungrouped = features.filter((f) => !groupedKeys.has(f));

  return (
    <div className="feature-list-root">
      {renderedGroups.map((group) => (
        <div key={group.label} className="feature-group">
          <div className="feature-group-header">
            <span className="feature-group-icon">{group.icon}</span>
            <span className="feature-group-label">{t(group.labelKey)}</span>
            <span className="feature-group-count">{group.matched.length}</span>
          </div>
          <div className="feature-group-items">
            {group.matched.map((key) => (
              <div key={key} className="feature-item">
                <CheckOutlined className="feature-check" />
                <span>{getFeatureLabel(key)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {ungrouped.length > 0 && (
        <div className="feature-group">
          <div className="feature-group-header">
            <span className="feature-group-icon"><MdBuild /></span>
            <span className="feature-group-label">{t('feature_list.other')}</span>
            <span className="feature-group-count">{ungrouped.length}</span>
          </div>
          <div className="feature-group-items">
            {ungrouped.map((key) => (
              <div key={key} className="feature-item">
                <CheckOutlined className="feature-check" />
                <span>{getFeatureLabel(key)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureList;
